/**
 * Authentication Context with email/password authentication
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { TokenResponse } from '@/services/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

interface User {
  email: string;
  displayName?: string;
  role: 'admin' | 'team_lead' | 'member';
  teamIds: string[];      // Teams where user is team_lead
  allTeamIds: string[];   // All teams user belongs to
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  hasTeamAccess: (teamId: string) => boolean;
  isTeamLead: (teamId: string) => boolean;
  handleLogin: (response: TokenResponse) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('auth_token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      }
    }
    setIsLoading(false);
  }, []);

  // Handle login response from API
  const handleLogin = useCallback((response: TokenResponse) => {
    const { access_token, user: userData } = response;

    const newUser: User = {
      email: userData.email,
      displayName: userData.displayName || userData.email.split('@')[0],
      role: (userData.role as User['role']) || 'member',
      isAdmin: userData.is_admin,
      teamIds: userData.team_ids || [],
      allTeamIds: userData.all_team_ids || [],
    };

    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    // Optionally call backend logout
    fetch(`${API_URL}/auth/logout`, { method: 'POST' }).catch(() => {});
  }, []);

  // Check if user has access to a team
  const hasTeamAccess = useCallback(
    (teamId: string): boolean => {
      if (!user) return false;
      if (user.isAdmin) return true;
      return user.allTeamIds.includes(teamId);
    },
    [user]
  );

  // Check if user is team lead for a specific team
  const isTeamLead = useCallback(
    (teamId: string): boolean => {
      if (!user) return false;
      if (user.isAdmin) return true;
      return user.teamIds.includes(teamId);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        logout,
        hasTeamAccess,
        isTeamLead,
        handleLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
